/**
 * What a downloadable file actually is, so the viewer can preview it in the browser. The backend's
 * asset `type` is coarse (MP3s and DOCX both arrive as `document`), so the file is inspected itself:
 * response headers first, then the first bytes, then the file name.
 */
export type FileKind =
  | 'pdf'
  | 'image'
  | 'audio'
  | 'video'
  | 'docx'
  | 'sheet'
  | 'pptx'
  | 'html'
  | 'text'
  | 'unknown';

export interface FileInfo {
  kind: FileKind;
  /** Lower-case extension without the dot, when one could be found. */
  ext: string | null;
  name: string | null;
  mime: string | null;
  size: number | null;
}

const EXT_KIND: Record<string, FileKind> = {
  pdf: 'pdf',
  png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', webp: 'image', avif: 'image', svg: 'image', bmp: 'image', ico: 'image',
  mp3: 'audio', wav: 'audio', m4a: 'audio', aac: 'audio', ogg: 'audio', oga: 'audio', flac: 'audio', opus: 'audio',
  mp4: 'video', m4v: 'video', webm: 'video', mov: 'video', ogv: 'video',
  docx: 'docx', docm: 'docx', dotx: 'docx',
  xlsx: 'sheet', xlsm: 'sheet', xlsb: 'sheet', xls: 'sheet', ods: 'sheet', csv: 'sheet', tsv: 'sheet', numbers: 'sheet',
  pptx: 'pptx', ppsx: 'pptx', potx: 'pptx',
  html: 'html', htm: 'html',
  txt: 'text', md: 'text', markdown: 'text', json: 'text', xml: 'text', yaml: 'text', yml: 'text', log: 'text',
  ini: 'text', toml: 'text', rtf: 'text', js: 'text', ts: 'text', tsx: 'text', jsx: 'text', css: 'text',
  py: 'text', java: 'text', c: 'text', cpp: 'text', h: 'text', go: 'text', rs: 'text', rb: 'text', php: 'text',
  sh: 'text', sql: 'text', swift: 'text', kt: 'text', srt: 'text', vtt: 'text',
};

const MIME_KIND: Array<[RegExp, FileKind]> = [
  [/^application\/pdf/, 'pdf'],
  [/^image\//, 'image'],
  [/^audio\//, 'audio'],
  [/^video\//, 'video'],
  [/wordprocessingml/, 'docx'],
  [/spreadsheetml|ms-excel|opendocument\.spreadsheet|^text\/(csv|tab-separated-values)/, 'sheet'],
  [/presentationml/, 'pptx'],
  [/^text\/html/, 'html'],
  [/^text\/|^application\/(json|xml|javascript|x-yaml|yaml|rtf)/, 'text'],
];

export function extensionOf(name: string | null | undefined): string | null {
  if (!name) return null;
  const clean = name.split(/[?#]/)[0] ?? '';
  const match = /\.([a-z0-9]{1,8})$/i.exec(clean);
  return match?.[1] ? match[1].toLowerCase() : null;
}

function nameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*\s*=\s*[^']*''([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
    } catch {
      /* fall through to the plain filename */
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() ?? null;
}

function startsWith(bytes: Uint8Array, sig: number[], offset = 0) {
  return sig.every((b, i) => bytes[offset + i] === b);
}

function ascii(bytes: Uint8Array, from: number, to: number) {
  return String.fromCharCode(...bytes.subarray(from, to));
}

/** Magic numbers for the formats the viewer can render; `zip` and `ole` need the name to decide. */
function kindFromBytes(bytes: Uint8Array): FileKind | 'zip' | 'ole' | null {
  if (bytes.length < 4) return null;
  if (ascii(bytes, 0, 5) === '%PDF-') return 'pdf';
  if (startsWith(bytes, [0x50, 0x4b, 0x03, 0x04])) return 'zip';
  if (startsWith(bytes, [0xd0, 0xcf, 0x11, 0xe0])) return 'ole';
  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47]) || startsWith(bytes, [0xff, 0xd8, 0xff]) || ascii(bytes, 0, 4) === 'GIF8') return 'image';
  if (ascii(bytes, 0, 4) === 'RIFF') {
    const form = ascii(bytes, 8, 12);
    if (form === 'WEBP') return 'image';
    if (form === 'WAVE') return 'audio';
    if (form === 'AVI ') return 'video';
  }
  if (ascii(bytes, 0, 3) === 'ID3' || startsWith(bytes, [0xff, 0xfb]) || ascii(bytes, 0, 4) === 'fLaC' || ascii(bytes, 0, 4) === 'OggS') return 'audio';
  if (ascii(bytes, 4, 8) === 'ftyp') return /^M4A|^M4B/.test(ascii(bytes, 8, 11)) ? 'audio' : 'video';
  if (startsWith(bytes, [0x1a, 0x45, 0xdf, 0xa3])) return 'video';
  const head = new TextDecoder('utf-8', { fatal: false }).decode(bytes).trimStart().toLowerCase();
  if (head.startsWith('<!doctype html') || head.startsWith('<html')) return 'html';
  if (head.startsWith('<svg') || (head.startsWith('<?xml') && head.includes('<svg'))) return 'image';
  if (head.startsWith('{\\rtf')) return 'text';
  return looksLikeText(bytes) ? 'text' : null;
}

function looksLikeText(bytes: Uint8Array) {
  let control = 0;
  for (const b of bytes) {
    if (b === 0) return false;
    if (b < 9 || (b > 13 && b < 32)) control += 1;
  }
  return control / Math.max(bytes.length, 1) < 0.02;
}

function fromZipOrOle(container: 'zip' | 'ole', ext: string | null, mime: string | null): FileKind {
  const byName = ext ? EXT_KIND[ext] : undefined;
  const byMime = mime ? MIME_KIND.find(([re]) => re.test(mime))?.[1] : undefined;
  const hinted = byName ?? byMime;
  if (container === 'ole') return hinted === 'sheet' ? 'sheet' : 'unknown';
  if (hinted === 'docx' || hinted === 'sheet' || hinted === 'pptx') return hinted;
  // A bare .zip with no hint: the viewers that take OOXML can still tell which one it is.
  return 'unknown';
}

const SNIFF_BYTES = 512;

/**
 * Resolves what a file is with a single small range request. Works whether the server honours the
 * range (206) or streams the whole body (200): only the first chunk is read, then the body is dropped.
 */
export async function inspectFile(url: string, init: RequestInit, nameHint?: string | null): Promise<FileInfo> {
  const controller = new AbortController();
  const headers = new Headers(init.headers);
  headers.set('Range', `bytes=0-${SNIFF_BYTES - 1}`);
  try {
    const res = await fetch(url, { ...init, headers, signal: controller.signal });
    if (!res.ok) {
      controller.abort();
      return { kind: 'unknown', ext: extensionOf(nameHint), name: nameHint ?? null, mime: null, size: null };
    }
    const mime = (res.headers.get('content-type') ?? '').split(';')[0]?.trim().toLowerCase() || null;
    const name = nameFromDisposition(res.headers.get('content-disposition')) ?? nameHint ?? null;
    const ext = extensionOf(name) ?? extensionOf(new URL(url, window.location.href).pathname);
    const range = /\/(\d+)\s*$/.exec(res.headers.get('content-range') ?? '');
    const length = Number(res.headers.get('content-length'));
    const size = range?.[1] ? Number(range[1]) : res.status === 200 && length > 0 ? length : null;

    let bytes = new Uint8Array();
    const reader = res.body?.getReader();
    if (reader) {
      const chunks: Uint8Array[] = [];
      let total = 0;
      while (total < SNIFF_BYTES) {
        const { done, value } = await reader.read();
        if (done || !value) break;
        chunks.push(value);
        total += value.length;
      }
      void reader.cancel().catch(() => undefined);
      bytes = new Uint8Array(Math.min(total, SNIFF_BYTES));
      let at = 0;
      for (const chunk of chunks) {
        const take = chunk.subarray(0, bytes.length - at);
        bytes.set(take, at);
        at += take.length;
        if (at >= bytes.length) break;
      }
    }
    controller.abort();

    const sniffed = kindFromBytes(bytes);
    const generic = !mime || mime === 'application/octet-stream' || mime === 'binary/octet-stream';
    const byMime = generic ? undefined : MIME_KIND.find(([re]) => re.test(mime))?.[1];
    const byExt = ext ? EXT_KIND[ext] : undefined;

    let kind: FileKind;
    if (sniffed === 'zip' || sniffed === 'ole') kind = fromZipOrOle(sniffed, ext, mime);
    // Bytes are the strongest signal for binary formats; a CSV or JSON file only differs from
    // plain text by its name or declared type.
    else if (sniffed && sniffed !== 'text') kind = sniffed;
    else if (sniffed === 'text') kind = byExt === 'sheet' || byMime === 'sheet' ? 'sheet' : byExt === 'html' || byMime === 'html' ? 'html' : 'text';
    else kind = byMime ?? byExt ?? 'unknown';

    return { kind, ext, name, mime, size };
  } catch {
    controller.abort();
    const ext = extensionOf(nameHint);
    return { kind: (ext && EXT_KIND[ext]) || 'unknown', ext, name: nameHint ?? null, mime: null, size: null };
  }
}
